import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// Persona slug → display name. Actual auth is passwordless — the backend
// `/api/demo/token` endpoint is the source of truth for which slugs are
// allowed. This map is display-only.
const PERSONAS = {
  aria: { label: "Aria" },
  iris: { label: "Iris" },
  maya: { label: "Maya" },
  mateo: { label: "Mateo" },
};

// One-tap demo: auto-logs in as a seeded demo account so visitors can poke
// around without creating one. Accepts EITHER `?profile=...` (canonical)
// or `?as=...` (alias kept for backwards-compatibility with previously-shared
// links). Defaults to Aria.
//
// Uses the passwordless `/api/demo/token` endpoint so demo links keep
// working even if the demo users' password hashes drift (which broke the
// prior password-based flow on production and dumped Google testers onto
// the login screen).
//
// Critical: this route ALWAYS logs out the current visitor first, even if
// they're already signed in as a real account. Otherwise the route silently
// dumped real users onto their own empty Discover deck (matched everyone
// already → "no one to swipe through") instead of running the demo flow.
export default function Demo() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { logout, loginDemo } = useAuth();
  const slug = ((params.get("profile") || params.get("as") || "aria")).toLowerCase();
  const persona = PERSONAS[slug] || PERSONAS.aria;
  const [status, setStatus] = useState(`Signing you in as ${persona.label}...`);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        // Force a clean session so even already-logged-in visitors get the
        // demo persona, never their own (potentially empty) account.
        logout();
        await loginDemo(slug);
        // Refresh the demo persona's deck so every visitor lands on a full set.
        // Fire-and-forget — a slow reset should not block the redirect.
        api.post(`/demo/reset-deck?profile=${slug}`).catch(() => {});
        nav("/discover", { replace: true });
      } catch (err) {
        // Log the actual backend error so we can debug production failures.
        // eslint-disable-next-line no-console
        console.error("Demo sign-in failed", err?.response?.data || err?.message || err);
        const detail = err?.response?.data?.detail;
        setStatus(
          typeof detail === "string" && detail.length < 120
            ? `Demo unavailable: ${detail}`
            : "Demo unavailable right now — opening Sign in."
        );
        setTimeout(() => nav("/login", { replace: true }), 2000);
      }
    })();
  }, [nav, loginDemo, logout, slug, persona]);

  return (
    <div
      data-testid="demo-loader"
      className="min-h-[100svh] flex flex-col items-center justify-center bg-[#16171A] text-[#F0E6D2] px-6"
    >
      <div className="w-14 h-14 rounded-full bg-[#B95F73] flex items-center justify-center shadow-xl shadow-[#B95F73]/40 mb-6">
        <Sparkles className="w-7 h-7 text-[#F0E6D2]" fill="#F0E6D2" />
      </div>
      <h1 className="font-display text-3xl font-light tracking-tight mb-3">
        Spark <span className="font-display-italic text-[#B95F73]">demo</span>
      </h1>
      <p className="text-[#A0A0A8] text-sm font-display italic mb-6">{status}</p>
      <Loader2 className="w-6 h-6 animate-spin text-[#C9A961]" />
    </div>
  );
}
